import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FilterService } from './filter.service';
import { FormControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  Router,
  RoutesRecognized,
} from '@angular/router';
import { Filter } from './filter.model';
import { CurrencyService } from '../shared/currency.service';
import { UserService } from '../shared/user/user.service';
import { AnalyticsService } from '../shared/analytics/analytics.service';
import { REGIONS } from '../app.constants';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';

type FilterFormModel = FormGroup<{
  category: FormControl<string>;
  regions: FormArray<FormControl<boolean>>;
  country: FormControl<string>;
  bandGroup: FormControl<string>;
  band: FormControl<string>;
}>;

@Component({
  selector: 'app-filter',
  templateUrl: './filter.component.html',
  styleUrls: ['./filter.component.scss'],
})
export class FilterComponent implements OnInit, OnDestroy {
  @Input() filterData: any;

  filtersForm: FilterFormModel;
  countries: any[] = [];
  bands: any[] = [];
  regionsArray: string[] = REGIONS;
  currency: string = this.currencyService.getCurrency();
  isReport: boolean = false;
  selectedReport: string = 'upcoming';

  private destroy$ = new Subject<void>();
  private isInternalUpdate = false;

  constructor(
    public filterService: FilterService,
    private userService: UserService,
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    public currencyService: CurrencyService,
    private analyticsService: AnalyticsService,
    private translateService: TranslateService
  ) {
    const regionControls = this.regionsArray.map((region) => {
      const hasAccess = this.userService.userHasAccessToRegion(region);
      return new FormControl<boolean>(hasAccess);
    });

    this.filtersForm = this.fb.group({
      category: 'licences',
      regions: new FormArray(regionControls),
      country: '',
      bandGroup: '',
      band: '',
    });
  }

  ngOnInit(): void {
    const selectedRegions = this.userService.getRegions();
    this.resetCountries(selectedRegions);
    this.updateFilterPanel(this.route.firstChild.snapshot, selectedRegions);

    this.router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe((routerEvent) => {
        if (routerEvent instanceof RoutesRecognized) {
          const route = routerEvent.state.root.firstChild;
          const currentRegions = route.queryParams.regionCode || this.userService.getRegions();
          this.updateFilterPanel(route, currentRegions);
        }
      });

    this.setupAutoNavigation();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupAutoNavigation(): void {
    this.filtersForm.get('regions').valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(300)
      )
      .subscribe(() => {
        if (!this.isInternalUpdate) {
          const selectedRegions = this.getSelectedRegions();
          this.resetCountries(selectedRegions);
          this.navigateWithCurrentFilters('regionChange');
        }
      });

    this.filtersForm.get('country').valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(300)
      )
      .subscribe(() => {
        if (!this.isInternalUpdate) {
          this.navigateWithCurrentFilters('countryChange');
        }
      });

    this.filtersForm.get('bandGroup').valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(300)
      )
      .subscribe(() => {
        if (!this.isInternalUpdate) {
          this.navigateWithCurrentFilters('bandGroupChange');
        }
      });

    this.filtersForm.get('band').valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(300)
      )
      .subscribe(() => {
        if (!this.isInternalUpdate) {
          this.navigateWithCurrentFilters('bandChange');
        }
      });
  }

  isActiveTab(tabName: string): boolean {
    return this.filtersForm.value.category === tabName;
  }

  isRegionDisabled(region: string): boolean {
    return !this.userService.userHasAccessToRegion(region);
  }

  getSelectedRegions(): string[] {
    const selectedRegions: string[] = [];
    const regionsControls = this.filtersForm.controls.regions.controls;

    regionsControls.forEach((control, index) => {
      if (control.value) {
        selectedRegions.push(this.regionsArray[index]);
      }
    });

    return selectedRegions;
  }

  resetCountries(selectedRegions: string[]): void {
    this.countries = [];
    let regionsObject = this.filterData.regions;

    if (selectedRegions?.length > 0) {
      regionsObject = regionsObject.filter((regionObject) =>
        selectedRegions.includes(regionObject.region)
      );
    }

    for (const regionObject of regionsObject) {
      this.countries.push(...regionObject.countries);
    }

    this.countries.sort((a, b) => {
      const nameA = this.translateService.instant('CountryNames.' + a);
      const nameB = this.translateService.instant('CountryNames.' + b);
      return nameA.localeCompare(nameB);
    });

    this.filtersForm.patchValue({ country: '' }, { emitEvent: false });
  }

  resetBandGroup(bandGroup: string): void {
    this.isInternalUpdate = true;

    if (!bandGroup) {
      this.bands = [];
      for (const bandGroupObject of this.filterData.bandGroups) {
        this.bands.push(...bandGroupObject.bands);
      }
    } else {
      const foundGroup = this.filterData.bandGroups.find(
        (bg) => bg.bandGroup === bandGroup
      );
      this.bands = foundGroup?.bands || [];
    }

    this.filtersForm.patchValue({ band: '' }, { emitEvent: false });

    requestAnimationFrame(() => {
      this.isInternalUpdate = false;
    });
  }

  onClickBandgroup(event: any): void {
    this.resetBandGroup(event.target.value);
  }

  getBandGroup(bandCode: string): string {
    for (const bandGroupObject of this.filterData.bandGroups) {
      const foundBand = bandGroupObject.bands.find(
        (band) => band.code === bandCode
      );
      if (foundBand) {
        return bandGroupObject.bandGroup;
      }
    }
    return '';
  }

  private updateFilters(filter: Filter): void {
    this.isInternalUpdate = true;

    const regionsFormArray = this.filtersForm.get('regions') as FormArray;

    this.regionsArray.forEach((region, index) => {
      const shouldBeChecked = filter.regions.includes(region);
      const control = regionsFormArray.at(index);

      if (control.value !== shouldBeChecked) {
        control.patchValue(shouldBeChecked, { emitEvent: false });
      }

      if (this.isRegionDisabled(region)) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    });

    this.filtersForm.patchValue({
      category: filter.category,
      country: filter.country,
      bandGroup: filter.bandGroup,
      band: filter.band,
    }, { emitEvent: false });

    setTimeout(() => {
      this.isInternalUpdate = false;
    }, 50);
  }

  private updateFilterPanel(
    route: ActivatedRouteSnapshot,
    selectedRegions: string[]
  ): void {
    const subUrl = route.url.join('/');
    const filter = new Filter();

    if (subUrl.includes('awards')) {
      filter.category = 'awards';
    } else if (subUrl.includes('licences')) {
      filter.category = 'licences';
    } else if (subUrl.includes('reports')) {
      filter.category = 'reports';

      if (subUrl.includes('operators')) {
        this.selectedReport = 'operators';
      } else if (subUrl.includes('upcoming')) {
        this.selectedReport = 'upcoming';
      }
    }

    this.isReport = subUrl.includes('reports');

    if (route.queryParams.regionCode) {
      filter.regions = route.queryParams.regionCode;
      selectedRegions = route.queryParams.regionCode;
    } else {
      filter.regions = selectedRegions;
    }

    this.resetCountries(selectedRegions);

    if (route.params.countryCode) {
      filter.country = route.params.countryCode;
    }

    let bandGroup = route.params.bandGroup;
    if (bandGroup) {
      filter.bandGroup = bandGroup;
    }

    const band = route.params.bandCode;
    if (band) {
      bandGroup = this.getBandGroup(band);
      filter.bandGroup = bandGroup;
      filter.band = band;
    }

    this.resetBandGroup(bandGroup);

    if (route.params.awardKey) {
      filter.country = route.params.awardKey.substring(0, 2);
    }

    this.updateFilters(filter);
  }

  private navigateWithCurrentFilters(source: string): void {
    const selectedRegions = this.getSelectedRegions();
    const categoryValue = this.filtersForm.get('category').value;
    const countryValue = this.filtersForm.get('country').value;
    const bandGroupValue = this.filtersForm.get('bandGroup').value;
    const bandValue = this.filtersForm.get('band').value;

    this.analyticsService.pushEventData({
      event: 'autoNavigation',
      source: source,
      Webapp: 'Spectrum'
    });

    let routerUrl: string;

    if (categoryValue === 'reports') {
      routerUrl = `/reports/${this.selectedReport}`;
    } else {
      routerUrl = this.filterService.buildRouterUrl(
        categoryValue,
        countryValue,
        bandGroupValue,
        bandValue
      );
    }

    if (selectedRegions.length > 0) {
      this.router.navigate([routerUrl], {
        queryParams: { regionCode: selectedRegions },
      });
    } else {
      this.router.navigate([routerUrl]);
    }
  }

  onTabChange(tabName: string): void {
    this.analyticsService.pushEventData({
      event: 'tabClick',
      Webapp: 'Spectrum'
    });

    const selectedRegions = this.getSelectedRegions();
    const countryValue = this.filtersForm.get('country').value;
    const bandGroupValue = this.filtersForm.get('bandGroup').value;
    const bandValue = this.filtersForm.get('band').value;

    const routerUrl = this.filterService.buildRouterUrl(
      tabName,
      countryValue,
      bandGroupValue,
      bandValue
    );

    if (selectedRegions.length > 0) {
      this.router.navigate([routerUrl], {
        queryParams: { regionCode: selectedRegions },
      });
    } else {
      this.router.navigate([routerUrl]);
    }
  }

  onResetFilter(): void {
    const categoryValue = this.filtersForm.get('category').value;

    if (categoryValue === 'reports') {
      return;
    }

    this.analyticsService.pushEventData({
      event: 'resetFilter',
      Webapp: 'Spectrum'
    });

    const selectedRegions = this.getSelectedRegions();

    if (selectedRegions.length > 0) {
      this.router.navigate([`/${categoryValue}`], {
        queryParams: { regionCode: selectedRegions },
      });
    } else {
      this.router.navigate([`/${categoryValue}`]);
    }
  }

  isResetDisabled(): boolean {
    return this.isReport;
  }

  onReportChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    const selectedRegions = this.getSelectedRegions();

    if (value === 'upcoming') {
      this.router.navigate(['/reports/upcoming'], {
        queryParams: selectedRegions.length ? { regionCode: selectedRegions } : {}
      });
    } else if (value === 'operators') {
      this.router.navigate(['/reports/operators'], {
        queryParams: selectedRegions.length ? { regionCode: selectedRegions } : {}
      });
    }
  }
}